import { Button } from "components/commons/Button";
import Icon from "components/commons/Icon";
import { Link } from "components/commons/Link";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useRouter } from "next/router";
import { localizePath } from "src/utils/localizedRoutes";
import CheckCircle from "@/assets/img/icons/check-circle-green.svg";

// Kept in sync with Banner.tsx's FADE_DURATION: the outgoing slide's
// background fades out immediately while the incoming one waits for that
// to finish before fading in, so two slides' content never overlaps.
const FADE_DURATION = 0.4;

type BannerBackground = {
  desktopMp4: string;
  desktopWebm: string;
  desktopPoster: string;
  mobileMp4: string;
  mobileWebm: string;
  mobilePoster: string;
};

export type BannerSlideProps = {
  active: boolean;
  badge: React.ReactNode;
  titlePart1: string;
  titlePart2: string;
  accentClassName: string;
  description: string;
  primaryHref: string;
  primaryLabel: string;
  primaryIcon?: React.ReactNode;
  primaryClassName: string;
  secondaryHref: string;
  secondaryLabel: string;
  trustItems: [string, string, string];
  background: BannerBackground;
};

const BannerSlide = ({
  active,
  badge,
  titlePart1,
  titlePart2,
  accentClassName,
  description,
  primaryHref,
  primaryLabel,
  primaryIcon,
  primaryClassName,
  secondaryHref,
  secondaryLabel,
  trustItems,
  background,
}: BannerSlideProps) => {
  const router = useRouter();
  // Only one slide should ever be an <h1> at a time (the active one) to avoid
  // multiple-H1s-on-page; inactive slides still render their heading text
  // (as a <p>) so crawlers index all slides' copy, not just the active one.
  const HeadingTag = active ? "h1" : "p";

  return (
    <div className="relative flex items-center overflow-hidden w-100vw min-h-[100svh] pt-20 px-4">
      <div className="absolute w-screen h-[100svh] -z-10 top-0 left-0">
        <AnimatePresence>
          {active && (
            <motion.div
              key="background"
              className="absolute inset-0 h-full w-full"
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: { duration: FADE_DURATION, delay: FADE_DURATION },
              }}
              exit={{
                opacity: 0,
                transition: { duration: FADE_DURATION, delay: 0 },
              }}
            >
              <video
                className="hidden md:block absolute inset-0 w-full h-full object-cover opacity-35"
                poster={background.desktopPoster}
                muted
                loop
                autoPlay
                playsInline
              >
                <source src={background.desktopWebm} type="video/webm" />
                <source src={background.desktopMp4} type="video/mp4" />
              </video>
              <video
                className="md:hidden absolute inset-0 w-full h-full object-cover opacity-35"
                poster={background.mobilePoster}
                muted
                loop
                autoPlay
                playsInline
              >
                <source src={background.mobileWebm} type="video/webm" />
                <source src={background.mobileMp4} type="video/mp4" />
              </video>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="w-full mx-auto max-w-7xl">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto lg:items-start lg:text-left lg:mx-0">
          <div className="hidden md:block">{badge}</div>
          <HeadingTag className="mt-4 text-7xl font-bold leading-[1.1] text-center text-white md:text-11xl md:font-extrabold lg:text-left">
            {titlePart1}
            <strong className={`${accentClassName} lg:font-extrabold`}>
              {" "}
              {titlePart2}
            </strong>
          </HeadingTag>
          <p className="text-white text-center font-light text-lg mt-2 leading-8 lg:text-left lg:text-xl">
            {description}
          </p>
          <div className="w-full justify-center mt-8 flex flex-col space-y-4 lg:w-auto lg:flex-row lg:justify-start lg:space-y-0 lg:space-x-4">
            <Link href={primaryHref} prefetch={false}>
              <Button
                variant="primary"
                className={`inline-flex justify-center w-full lg:w-auto ${primaryClassName}`}
              >
                {primaryIcon}
                {primaryLabel}
              </Button>
            </Link>
            <Link href={localizePath(secondaryHref, router.locale)} prefetch={false}>
              <Button
                variant="secondary"
                className="bg-transparent border-white w-full lg:w-auto"
              >
                {secondaryLabel}
              </Button>
            </Link>
          </div>
          <div className="hidden md:flex w-full flex-col items-center gap-2 mt-6 text-white text-sm font-semibold sm:flex-row sm:justify-center sm:gap-6 lg:w-auto lg:justify-start">
            {trustItems.map((item) => (
              <span key={item} className="flex items-center gap-1.5">
                <Icon src={CheckCircle} alt="" width={16} height={16} />
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerSlide;
